import type { ComponentRef, ErrorHandler } from '@angular/core'
import { inject, Injectable, Injector } from '@angular/core'
import { ErrorModalComponent } from './_shared/components/misc/error-modal/error-modal.component'
import { DynamicRefService } from './_shared/services/dynamic-ref-service/dynamic-ref.service'
import { HttpErrorResponse, HttpResponse } from '@angular/common/http'
import { Router } from '@angular/router'
import { ToastService } from './_shared/services/toast-service/toast.service'
import { getSync } from 'stacktrace-js'
import { LogService } from '@awork/_shared/services/log-service/log.service'
import { Level } from '@awork/_shared/services/log-service/types'
import { classifyError, ErrorType } from '@awork/_shared/functions/classify-error'
import { ModalService } from './_shared/services/modal-service/modal.service'
import { FeatureFlag } from '@awork/_shared/state/feature-flag.store'
import { FeatureFlagQuery } from '@awork/_shared/state/feature-flag.query'
import { UserQuery } from '@awork/features/user/state/user.query'
import { AppQuery } from '@awork/core/state/app.query'
import { AppStore } from '@awork/core/state/app.store'
import { AccountService } from '@awork/_shared/services/account-service/account.service'

@Injectable({ providedIn: 'root' })
export class AppErrorHandler implements ErrorHandler {
  private dynRefSvc = inject(DynamicRefService)
  private injector = inject(Injector)
  private logService = inject(LogService)
  private featureFlagQuery = inject(FeatureFlagQuery)
  private userQuery = inject(UserQuery)
  private appQuery = inject(AppQuery)
  private appStore = inject(AppStore)
  private accountService = inject(AccountService)

  protected readonly experimentalFeaturesEnabled = this.featureFlagQuery.queryFlag(
    FeatureFlag.loggingExperimentalFeatures
  )
  private currentUser = this.userQuery.queryCurrentUser()
  private isUserLoading = this.userQuery.queryLoading
  private isLoggedIn = this.appQuery.queryIsLoggedIn()

  private modal: ErrorModalComponent | null
  private modalRef: ComponentRef<ErrorModalComponent> | null
  private expectedErrors: { message?: string; reason: string; action?: Function; matches?: Function }[] = [
    {
      message: `You provided 'null' where a stream was expected`,
      reason: `LEAKING_SUBSCRIPTION`
    },
    {
      message: 'The selector "aw-app-root" did not match any elements',
      reason: `UNSUPPORTED_BROWSER`
    },
    {
      message: 'Uncaught (in promise): [object Undefined]',
      reason: 'ADBLOCK'
    },
    {
      message: `UnknownError: Failed to execute 'put' on 'Cache': Unexpected internal error.`,
      reason: 'SERVICEWORKER'
    },
    {
      message: 'math.pow is not a function',
      reason: 'LOTTIE'
    },
    {
      matches: (error: Error) =>
        (error.stack && error.stack.includes('@aspnet/signalr')) ||
        (error.message &&
          (error.message.includes('Error parsing handshake response') ||
            error.message.includes('WebSocket') ||
            error.message.includes('at new HttpError') ||
            error.message.includes('Server returned an error on close') ||
            error.message.includes('Server timeout elapsed without receiving a message') ||
            error.message.includes(`Cannot send data if the connection is not in the 'Connected' State`))) ||
        this.matchStackFrame('@aspnet/signalr'),
      reason: 'SIGNALR'
    },
    {
      matches: (error: Error) => error.message?.includes('ChunkLoadError'),
      reason: 'NEW_DEPLOYMENT',
      action: () => window.location.reload()
    },
    {
      message: 'Cannot parse url',
      reason: 'MANUAL_URL_EDIT',
      action: () => {
        const router: Router = this.injector.get(Router)
        router.navigate(['/404'], { skipLocationChange: true })
      }
    },
    {
      matches: (error: Error) =>
        error.message?.includes('REPLY_TIMEOUT') || error.message?.includes('Action timed out'),
      reason: 'REPLY_TIMEOUT'
    },
    {
      matches: (error: Error) =>
        error.stack?.includes('@angular/core') && error.message?.includes(`Cannot read property 'context' of null`),
      reason: 'ANGULAR_INTERNAL_ERROR'
    },
    {
      matches: (error: Error) =>
        error.message?.includes(`Internal error committing transaction.`) ||
        error.message?.includes(`DataError: Failed to write blobs`),
      reason: 'LOCAL_STORAGE_INTERNAL_ERROR'
    },
    {
      matches: (error: Error) =>
        error.stack?.includes('chargebee.js') ||
        (error.stack?.includes('chargebee') && error.message?.includes('ChunkLoadError')),
      reason: 'CHARGEBEE_INTERNAL_ERROR'
    },
    {
      matches: (error: Error) =>
        (error.message?.includes('undefined is not an object (evaluating ') &&
          error.message?.includes(`clientHeight`)) ||
        error.stack?.includes('smoothscroll.js') ||
        error.stack?.includes('hasScrollableSpace'),
      reason: 'SMOOTHSCROOLL_INTERNAL_ERROR'
    },
    {
      matches: (error: Error) => error.stack?.includes('medium-editor.js'),
      reason: 'MEDIUMEDITOR_INTERNAL_ERROR'
    },
    {
      matches: (error: Error) =>
        // https://sentry.bwork.io/organizations/hqlabs/issues/716/
        // https://github.com/angular/angular/issues/31684
        error.stack?.includes('errors occurred during unsubscription') ||
        error.message?.includes('errors occurred during unsubscription'),
      reason: 'ANGULAR_ZONE_ERROR'
    },
    {
      matches: (error: Error) =>
        error.stack?.includes('AsyncAction') || error.message?.includes('executing a cancelled action'),
      reason: 'ANGULAR_ASYNC_ACTION_ERROR'
    },
    {
      matches: (error: Error) =>
        error.message?.includes('Cannot read properties of undefined') &&
        (error.stack?.includes('drag-drop.mjs') ||
          (error.stack?.includes('._sortItem') && error.stack?.includes('._updateActiveDropContainer'))),
      reason: 'ANGULAR_CDK_DRAG_DROP_SORT_ERROR'
    },
    {
      matches: (error: Error) =>
        // Angular animate.leave + @for reconciliation bug:
        // https://github.com/angular/angular/issues/63544
        // https://github.com/angular/angular/issues/64730
        // https://github.com/angular/angular/issues/64336
        // https://awork.sentry.io/issues/7681446774 (FRONTEND-WEB-TZ2)
        // https://awork.sentry.io/issues/7462331454 (FRONTEND-WEB-TCY)
        error.message?.includes('insertBefore') &&
        (error.message.includes('is not a child of this node') ||
          error.message.includes('Child to insert before is not a child')),
      reason: 'ANGULAR_ANIMATION_RECONCILIATION_ERROR'
    },
    {
      message: 'chargebee',
      reason: 'CHARGEBEE_INTERNAL_ERROR'
    },
    {
      matches: (error: Error) => error instanceof HttpErrorResponse || error instanceof HttpResponse,
      reason: 'HTTP_ERROR_RESPONSE'
    },
    {
      message: 'HttpErrorResponse',
      reason: 'HTTP_ERROR_RESPONSE'
    },
    {
      matches: (error: Error) =>
        error instanceof TypeError &&
        (error.message.includes('Failed to fetch') ||
          error.message.includes('Abgebrochen') ||
          error.message.includes('Die Netzwerkverbindung wurde unterbrochen')),
      reason: 'NETWORK_FAILURE',
      action: () => {
        const toastService: ToastService = this.injector.get(ToastService)
        toastService.show(q.translations.AppErrorHandler.networkFailure, {
          action: () => window.location.reload(),
          actionText: q.translations.AppErrorHandler.reloadPage
        })
      }
    },
    {
      matches: (error: Error) =>
        error.message &&
        (error.message.includes('QuotaExceededError') ||
          (error.message.includes('exceeded') && error.message.includes('quota'))),
      reason: 'FULL_STORAGE'
    },
    {
      message: 'responseText is only available if responseType is',
      reason: 'FIREFOX RESPONSE ERROR'
    },
    {
      message: 'Uncaught (in promise): [object Window]',
      reason: 'VENDOR_ERROR'
    },
    {
      matches: (error: Error) =>
        error.message?.includes('Outlet is not activated') ||
        error.message?.includes('Cannot activate an already activated outlet'),
      reason: 'ROUTER_ERROR'
    },
    {
      message: 'mutation operation was attempted on a database',
      reason: 'FIREFOX_INDEXEDDB'
    },
    {
      message: 'InvalidStateError: The object is in an invalid state',
      reason: 'SAFARI_INVALID_STATE'
    },
    {
      matches: (error: Error) =>
        error.message?.includes('DataError') ||
        error.message?.includes('Failed to write blobs') ||
        error.message?.includes('IDBDatabase'),
      reason: 'DB_ERROR'
    },
    {
      matches: (error: Error) =>
        error.message?.includes('Illegal invocation') &&
        (error.stack?.toLowerCase().includes('beacon') ||
          error.stack?.includes('bat.js') ||
          error.stack?.includes('logMessage')),
      reason: 'SEND_BEACON_ERROR'
    },
    {
      message: 'Invalid content for node tableCell',
      reason: 'EDITOR_EMPTY_TABLE_CELL'
    }
  ]

  // eslint-disable-next-line complexity
  public handleError(error: Error & { originalError?: Error }): void {
    // Some integrations wrap the actual error as `originalError`.
    error = error ? error.originalError || error : null

    // Filter expected errors, but send warning to LogDNA

    if (!this.currentUser() && this.isLoggedIn() && !this.isUserLoading()) {
      this.accountService.logout(
        true,
        false,
        false,
        {
          level: 'WARN',
          message: 'Current user missing while app is logged in',
          error: error instanceof Error ? error : undefined
        },
        true
      )

      return
    }

    if (error?.stack || error?.message) {
      for (const expectedError of this.expectedErrors) {
        const { message, matches, reason, action } = expectedError

        if ((message && error.message && error.message.includes(message)) || (matches && matches(error))) {
          if (
            reason !== 'NEW_DEPLOYMENT' &&
            reason !== 'HTTP_ERROR_RESPONSE' &&
            reason !== 'SIGNALR' &&
            reason !== 'ADBLOCK' &&
            reason !== 'FULL_STORAGE' &&
            reason !== 'FIREFOX RESPONSE ERROR' &&
            reason !== 'ROUTER_ERROR' &&
            reason !== 'FIREFOX_INDEXEDDB' &&
            reason !== 'DB_ERROR' &&
            reason !== 'SEND_BEACON_ERROR'
          ) {
            const errorInfo =
              error instanceof HttpErrorResponse && error.headers ? error.headers.get('trace-id') : error.stack

            this.logService.logMessage(Level.warn, `${reason}: ${errorInfo}`, { error })
          }

          if (action) {
            try {
              action()
            } catch (error) {
              if (error instanceof Error) {
                this.logService.logMessage(
                  Level.warn,
                  `Executing an error handler action threw another error: ${error.stack}`,
                  { error }
                )
              }
            }
          }

          return
        }
      }

      console.error(error)

      const experimentalFeaturesEnabled = this.experimentalFeaturesEnabled()

      const errorType = classifyError(error, undefined, experimentalFeaturesEnabled)

      if (errorType === ErrorType.App && this.modal && this.modalRef) {
        console.warn('Error modal is already open')

        return
      }

      const openedModals = this.getOpenedModalTitles()

      const logService: LogService = this.injector.get(LogService)
      const eventId = logService.logError(error, undefined, undefined, errorType, { openedModals })

      if (errorType !== ErrorType.App) {
        return
      }

      // Show error modal and shake awork

      const [modalRef, modal] = this.dynRefSvc.create(ErrorModalComponent)

      this.modal = modal
      this.modalRef = modalRef

      const shakeDuration = 400

      modalRef.setInput('error', error)
      modalRef.setInput('shakeDuration', shakeDuration)
      modalRef.setInput('eventId', eventId)

      this.shakeQ(shakeDuration)

      this.modal.hiding.subscribe(() => {
        this.modalRef = null
        this.modal = null
      })
    }
  }

  private shakeQ(duration: number) {
    // The Web Animations API is not fully supported in all major browsers yet.
    // See: https://developer.mozilla.org/en-US/docs/Web/API/Web_Animations_API#Browser_compatibility
    if (document.body && 'animate' in document.body) {
      const transforms = [
        'translateX(-25px)',
        'translateX(25px)',
        'translateX(-25px)',
        'translateX(25px)',
        'translateX(-25px)',
        'translateX(0px)'
      ].map(transform => ({ transform }))

      document.body.animate(transforms as Keyframe[], { duration, easing: 'linear' })
    }
  }

  /**
   * Matched the filename from the error's stack frames
   * @param {string} filename
   * @returns {boolean}
   */
  private matchStackFrame(filename: string): boolean {
    const stackFrames = getSync() || []
    return stackFrames.some(frame => {
      return frame.fileName.includes(filename)
    })
  }

  /**
   * Gets the opened modal titles to add them to the error log as extra info
   * @returns {string[]}
   */
  private getOpenedModalTitles(): string[] {
    const modalService = this.injector.get(ModalService)
    return modalService.modals?.map(modal => modal.title()) || []
  }
}
